import { Pencil, X } from 'lucide-react'
import { useEffect } from 'react'

import {
  formatearFechaGasto,
  formatearFechaHora,
  formatearMedioPago,
  formatearMontoGasto,
  formatearTipoDocumento,
  obtenerEstadoPago,
} from '../../utils/gastos'
import { formatearRut } from '../../utils/rut'

function Seccion({ titulo, children, ultima = false }) {
  return (
    <div
      className={
        ultima ? '' : 'mb-8 border-b border-slate-100 pb-8'
      }
    >
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {titulo}
      </h3>

      {children}
    </div>
  )
}

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-medium text-slate-800">
        {valor}
      </p>
    </div>
  )
}

function Monto({ etiqueta, valor }) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {etiqueta}
      </p>

      <p className="mt-1 text-lg font-bold text-slate-900">
        {formatearMontoGasto(valor)}
      </p>
    </div>
  )
}

function DetalleGastoModal({
  abierto,
  gasto,
  facturaVinculada = null,
  onCerrar,
  onEditar,
}) {
  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape') {
        onCerrar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, onCerrar])

  if (!abierto || !gasto) return null

  const estado = obtenerEstadoPago(gasto.estado_pago)

  const alHacerClicFuera = (event) => {
    if (event.target === event.currentTarget) {
      onCerrar?.()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="detalle-gasto-titulo"
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-6">
          <div>
            <h2
              id="detalle-gasto-titulo"
              className="text-xl font-bold text-slate-900"
            >
              Detalle del gasto
            </h2>

            <span
              className={`mt-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estado.clase}`}
            >
              {estado.etiqueta}
            </span>
          </div>

          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          {facturaVinculada && (
            <div className="mb-6 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800">
              <p className="font-semibold">
                Gasto generado desde Abastecimiento
              </p>

              <p className="mt-1">
                Corresponde a la factura N.º{' '}
                {facturaVinculada.numero_documento}. Los importes,
                la categoría, el proveedor y el documento no se pueden
                modificar ni anular desde Gastos; solo se actualiza el pago.
              </p>
            </div>
          )}

          <Seccion titulo="Información general">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Fecha del gasto"
                valor={formatearFechaGasto(gasto.fecha_gasto)}
              />

              <Dato
                etiqueta="Categoría"
                valor={gasto.categoria?.nombre || 'Sin categoría'}
              />

              <Dato
                etiqueta="Proveedor"
                valor={
                  gasto.proveedor?.razon_social || 'Sin proveedor'
                }
              />

              <Dato
                etiqueta="RUT del proveedor"
                valor={
                  gasto.proveedor?.rut
                    ? formatearRut(gasto.proveedor.rut)
                    : '—'
                }
              />

              <Dato
                etiqueta="Tipo de documento"
                valor={formatearTipoDocumento(
                  gasto.tipo_documento
                )}
              />

              <Dato
                etiqueta="Número de documento"
                valor={gasto.numero_documento || '—'}
              />

              <div className="sm:col-span-2">
                <Dato
                  etiqueta="Descripción"
                  valor={gasto.descripcion || '—'}
                />
              </div>
            </div>
          </Seccion>

          <Seccion titulo="Montos">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
              <Monto etiqueta="Neto" valor={gasto.monto_neto} />
              <Monto etiqueta="IVA" valor={gasto.monto_iva} />
              <Monto
                etiqueta="Exento"
                valor={gasto.monto_exento}
              />
              <Monto
                etiqueta="Otros impuestos"
                valor={gasto.otros_impuestos}
              />

              <div className="rounded-xl bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Total
                </p>

                <p className="mt-1 text-lg font-bold text-emerald-900">
                  {formatearMontoGasto(gasto.monto_total)}
                </p>
              </div>
            </div>
          </Seccion>

          <Seccion titulo="Pago">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Estado de pago"
                valor={estado.etiqueta}
              />

              <Dato
                etiqueta="Medio de pago"
                valor={formatearMedioPago(gasto.medio_pago)}
              />

              <Dato
                etiqueta="Fecha de vencimiento"
                valor={formatearFechaGasto(
                  gasto.fecha_vencimiento
                )}
              />

              <Dato
                etiqueta="Fecha de pago"
                valor={formatearFechaGasto(gasto.fecha_pago)}
              />
            </div>
          </Seccion>

          <Seccion titulo="Información adicional" ultima>
            <Dato
              etiqueta="Observaciones"
              valor={gasto.observaciones || 'Sin observaciones'}
            />

            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Fecha de creación"
                valor={formatearFechaHora(gasto.creado_en)}
              />

              <Dato
                etiqueta="Última modificación"
                valor={formatearFechaHora(gasto.actualizado_en)}
              />
            </div>
          </Seccion>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 p-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 sm:w-auto"
          >
            Cerrar
          </button>

          {onEditar && (
            <button
              type="button"
              onClick={() => onEditar(gasto.id)}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 sm:w-auto"
            >
              <Pencil size={18} />
              Editar gasto
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DetalleGastoModal
